package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.MyProfile
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.ContentWidth
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetBlackBadge
import com.swetdot.swetgram.ui.design.SwetCard
import com.swetdot.swetgram.ui.design.SwetDestructiveButton
import com.swetdot.swetgram.ui.design.SwetDialog
import com.swetdot.swetgram.ui.design.SwetConfirmDialog
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetImage
import com.swetdot.swetgram.ui.design.SwetImagePhase
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetSecondaryButton
import com.swetdot.swetgram.ui.design.SwetScreenTitle
import com.swetdot.swetgram.ui.design.SwetContentPhase
import com.swetdot.swetgram.ui.design.SwetStateHost
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.design.rememberSwetImage
import com.swetdot.swetgram.ui.design.swetAppear
import com.swetdot.swetgram.ui.design.swetFloatingNavClearance
import com.swetdot.swetgram.ui.design.swetPressable
import com.swetdot.swetgram.ui.design.swetContentPhase
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.text.text
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.handle_prefixed
import swetgram.shared.generated.resources.profile_cancel
import swetgram.shared.generated.resources.profile_close
import swetgram.shared.generated.resources.profile_edit
import swetgram.shared.generated.resources.profile_joined_on
import swetgram.shared.generated.resources.profile_qr_copy_link
import swetgram.shared.generated.resources.profile_qr_entry
import swetgram.shared.generated.resources.profile_qr_link_copied
import swetgram.shared.generated.resources.profile_qr_link_only
import swetgram.shared.generated.resources.profile_qr_scan_or_share
import swetgram.shared.generated.resources.profile_settings
import swetgram.shared.generated.resources.profile_title
import swetgram.shared.generated.resources.profile_try_again
import swetgram.shared.generated.resources.profile_unavailable_body_mine
import swetgram.shared.generated.resources.profile_unavailable_title

/**
 * My Profile -- SPS §13.1, SDS §15.1 and §27.9.
 *
 * Renders every item of §13.1: avatar, display name, @username, bio, the joined date, the QR code
 * and the settings entry.
 *
 * The JOINED DATE arrives on [MyProfile.joinedAt] rather than on the shared `domain.User`, and that
 * placement is the whole reason it can be drawn at all. `created_at` is required on every
 * `UserPublic`, so on paper it belongs on `User` -- but `User` is the cached row every chat header
 * and every message sender is built from, and adding a column to it is a cache migration plus a
 * change to every mapper and every test that constructs one. The profile-only type carries it to
 * the one screen §13.1 asks for it on, and nothing else moves. The cost, recorded rather than
 * hidden: `PublicProfileScreen` still cannot show a joined date, because it renders a `User`.
 *
 * Email is not shown here. SDS §15.1 forbids it publicly and §13.1 does not list it; it
 * belongs to Settings > Account, where the verification state is actionable.
 *
 * This file also hosts the small primitives the rest of the `ui.profile` package shares -- the
 * section header, the disclosure row and the confirmation dialog. They are hand-built from tokens
 * for the reason the chat composer is: the design system has no component of that shape, and
 * bending [SwetCard]/`SwetListRow` into one would be using the wrong object rather than a missing
 * token. If a later pass adds them to the design system, this section is what moves.
 */
@Composable
fun ProfileScreen(
    profile: MyProfile?,
    onEditProfile: () -> Unit,
    onOpenSettings: () -> Unit,
    modifier: Modifier = Modifier,
    error: UiText? = null,
    /** Null hides the banner's action: a failure nobody can act on should not offer a button. */
    onRetry: (() -> Unit)? = null,
    /** False until the first read settles, so a blank screen is not reported as an empty one. */
    loadedOnce: Boolean = true,
    qr: QrUiState = QrUiState(),
    /** Null hides the QR entry entirely, for a build where the sheet is not wired up. */
    onShowQr: (() -> Unit)? = null,
    onDismissQr: () -> Unit = {},
    /** Defaulted so no navigation graph has to pass it; overridden only by tests and previews. */
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    val colors = SwetgramThemeTokens.colors

    /*
     * THE FLOATING TAB BAR IS OVER THIS SCREEN, AND THIS SCREEN RESERVES THE ROOM FOR IT.
     *
     * The bar floats: it is drawn in the app frame's `Box` over whatever the destination put there,
     * inset from the left, right and bottom, with the canvas running behind it on all four sides. So
     * something underneath has to stop its last row from ending up in the covered band, and the app
     * has exactly one answer -- each scrollable reserves `swetFloatingNavClearance()` itself, which
     * is what `ChatListScreen` and `GroupsScreen` do in their `contentPadding`. This tab used to be
     * the exception: the navigation host padded it away instead, so its content stopped ABOVE the bar
     * and the bar sat on a bare strip. That was one of the two things that made this tab's bar look
     * unlike the inbox's; `ui/Navigation.kt` records the other (the canvas colour) and the removal.
     *
     * THE VALUE IS COMPUTED, NOT PICKED. `swetFloatingNavClearance()` is the bar's own two insets,
     * its 64dp of content and the LIVE system gesture inset -- 0dp on a three-button phone, ~24dp on
     * gesture-navigation Android, ~34dp on an iPhone. Any hand-written dp here is correct on one
     * class of device and wrong on the other two, which is a defect that never appears on the machine
     * it was written on.
     *
     * IT ALREADY CONTAINS THE SYSTEM INSET, which is why this screen adds NO `navigationBarsPadding`
     * of its own anywhere. Both together would be the gesture bar counted twice and a visible hole
     * under the settings card. `GroupDiscoverScreen` takes the plain `navigationBarsPadding` instead
     * for the opposite reason: it is a pushed route with no bar over it.
     */
    val navClearance = swetFloatingNavClearance()

    /*
     * RE-ENTERING THE TAB RE-READS, when there is a reason to.
     *
     * `MyProfileViewModel` clears its error on a successful load, so the banner was never sticky
     * for want of a reset -- it was sticky for want of anything that would ever load again. The
     * view model is scoped to the navigation entry and reads once in `init`; the Chats, Groups and
     * moderation tabs each carry a `LaunchedEffect(Unit)` in the navigation host that re-reads on
     * return, and this tab carries none. So a profile whose read failed showed the same sentence
     * about the same dead request for as long as the process lived.
     *
     * IT LIVES HERE RATHER THAN IN THE HOST for two reasons: the screen already receives every
     * value the decision needs, and a rule about when a surface is stale belongs with the surface
     * that would be showing the stale thing.
     *
     * KEYED ON `Unit`, NEVER ON `error`. Keyed on the error this becomes a retry loop: the retry
     * fails, the message changes or is re-set, the effect restarts, forever, with no user action
     * anywhere in the cycle. One attempt per arrival is the whole intent.
     *
     * The `!loadedOnce` half is not redundant with the view model's own `init` read. It costs
     * nothing when that read is still in flight -- `refresh()` is single-flight and returns
     * immediately -- and it is the recovery for a screen composed against a view model whose first
     * read never started.
     */
    LaunchedEffect(Unit) {
        // Null `onRetry` means the caller has no way to read again; nothing to trigger, and the
        // banner already draws without its action for the same reason.
        onRetry?.takeIf { profileNeedsReloadOnEntry(error, loadedOnce) }?.invoke()
    }

    Column(modifier.fillMaxSize()) {
        // THE SAME HEADER THE OTHER THREE TABS DRAW, which is the whole of this change.
        //
        // It was `SwetTopAppBar`: a `surface` #171717 bar with a divider under it, sitting on a
        // canvas the previous pass had just moved to pure black. Chats, Discover and Groups all
        // draw a 28sp title straight on the canvas with no bar and no rule. So the four tabs agreed
        // about the bottom navigation and disagreed about the top, and a tab root that alone has a
        // toolbar reads as a screen from a different app -- which is exactly the complaint the
        // navigation change was answering, one edge over.
        //
        // NO TRAILING ACTION, and that is a statement rather than an omission: Chats and Groups
        // each carry a compose glyph because each can create the thing it lists, and there is no
        // second profile to make. `SwetScreenTitle` drops the end inset when `action` is null, so
        // the title lands on §6.2's margin like every other screen's first pixel.
        SwetScreenTitle(title = stringResource(Res.string.profile_title))

        // Above the content, never instead of it: a failed refresh must not blank a profile the
        // user could still read.
        if (error != null) {
            SwetErrorBanner(message = error.resolve(), onRetry = onRetry)
        }

        SwetStateHost(
            phase = swetContentPhase(
                hasContent = profile != null,
                loadedOnce = loadedOnce,
                hasError = error != null,
            ),
            modifier = Modifier.fillMaxSize(),
        ) { phase -> when (phase) {
            // Nothing loaded and nothing settled yet. `SwetLoading` fills and centres, and the
            // region it fills runs under the floating bar, so the spinner is nudged up by exactly
            // what the bar covers rather than being centred against a window it does not own.
            SwetContentPhase.Loading -> SwetLoading(
                modifier = Modifier.padding(bottom = navClearance),
            )

            // Settled with nothing to show. The banner above already says why, so this offers
            // the way out rather than repeating the reason.
            SwetContentPhase.Empty,
            SwetContentPhase.Error -> SwetEmptyState(
                title = stringResource(Res.string.profile_unavailable_title),
                body = stringResource(Res.string.profile_unavailable_body_mine),
                // An `if` rather than `onRetry?.let { stringResource(...) }`: the lookup is a
                // composable call, and one that runs inside a lambda only sometimes invoked is
                // needless subtlety when the same decision spells out as an expression.
                actionText = if (onRetry != null) stringResource(Res.string.profile_try_again) else null,
                onAction = onRetry,
                // Same reason as above, on the branch where it costs something: `SwetEmptyState`
                // centres its stack in what it fills, and its "Try again" button is the only way
                // out of this state. Centred against the whole window it lands in the band the bar
                // covers on a short one (landscape, split-screen, multi-window). The inbox pads its
                // own empty state identically.
                modifier = Modifier.padding(bottom = navClearance),
            )

            SwetContentPhase.Content -> {
                val contentProfile = profile ?: return@SwetStateHost
                Column(
                modifier = Modifier
                    .align(Alignment.CenterHorizontally)
                    .widthIn(max = ContentWidth.profile)
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
                    /*
                     * THE CLEARANCE, AND ITS PLACE IN THIS CHAIN IS THE WHOLE OF IT.
                     *
                     * This is a `verticalScroll` `Column`, not a `LazyColumn`, so there is no
                     * `contentPadding` to put it in -- and the two places a `padding` can go here
                     * behave differently:
                     *
                     *  - BEFORE `verticalScroll`, it insets the VIEWPORT. The column would then stop
                     *    short of the bar, nothing would ever scroll behind it, and this tab would
                     *    keep the arrangement that made its bar look wrong -- which is precisely the
                     *    navigation host's old padding, moved one file over.
                     *  - AFTER it, as here, it pads the SCROLLED CONTENT. That is exactly what
                     *    `contentPadding` means on the other three tabs: the canvas runs to the
                     *    bottom edge and under the bar, and the extra length at the end is what lets
                     *    the settings card scroll clear of it. This is the mechanism being copied,
                     *    not a second one.
                     *
                     * SDS §6.2's horizontal margin rides on the same call rather than a second one,
                     * so there is one padding node here and no question about which order the two
                     * are in. Its value is unchanged.
                     */
                    .padding(
                        start = Space.screenMargin,
                        end = Space.screenMargin,
                        bottom = navClearance,
                    ),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(Modifier.height(Space.two))

                ProfileIdentity(
                    displayName = contentProfile.user.displayName,
                    username = contentProfile.user.username,
                    bio = contentProfile.user.bio,
                    // The dot is omitted on one's own profile: telling a user they are online
                    // while they are looking at the screen is noise, not information.
                    isOnline = null,
                    // WHAT THE AVATAR SITS ON, and it is `background.deep` now: this tab joined the
                    // other three on the canvas (`ui/Navigation.kt`), and nothing between the frame
                    // and this disc paints a fill of its own. The value is inert while [isOnline] is
                    // null -- it colours the presence dot's cut-out border and no dot is drawn here
                    // -- but a stale answer to "what is behind this" is the kind that comes true
                    // later, when somebody gives this screen a dot for a different reason.
                    surfaceColor = colors.backgroundDeep,
                    avatarUrl = contentProfile.user.avatarUrl,
                    hasBlackBadge = contentProfile.user.hasBlackBadge,
                )

                // SPS §13.1 and SDS §15.1's joined date. Omitted entirely when the date did not
                // parse -- see [MyProfile.joinedAt]. The same `dd.MM.yyyy` rendering the rest of
                // the app uses, so a date never reads one way here and another on the edit screen.
                joinedLabel(contentProfile, zone)?.let { label ->
                    Spacer(Modifier.height(Space.two))
                    Text(
                        text = label.resolve(),
                        style = MaterialTheme.typography.labelMedium,
                        color = colors.textSecondary,
                        textAlign = TextAlign.Center,
                    )
                }

                Spacer(Modifier.height(Space.two))

                SwetCard(
                    // Explicit: the column around it centres its children, and a card that sized
                    // itself to its widest row would sit in the middle at a different width on
                    // every account.
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(vertical = Space.half),
                ) {
                    ProfileNavRow(
                        title = stringResource(Res.string.profile_edit),
                        onClick = onEditProfile,
                    )
                    if (onShowQr != null) {
                        ProfileNavRow(
                            title = stringResource(Res.string.profile_qr_entry),
                            onClick = onShowQr,
                        )
                    }
                    ProfileNavRow(
                        title = stringResource(Res.string.profile_settings),
                        onClick = onOpenSettings,
                    )
                }

                // Air below the last card, on the 8dp scale. NOT the tab bar's room -- that is the
                // clearance on this column's own padding above, computed from the bar's geometry
                // and the live system inset. This is the ordinary end-of-content gap, and it is
                // what keeps the card from ending flush against the bar's top edge once the
                // clearance has done its job.
                Spacer(Modifier.height(Space.four))
                }
            }
        } }
    }

    if (qr.visible) {
        QrDialog(state = qr, onDismiss = onDismissQr)
    }
}

/**
 * The QR sheet.
 *
 * SPS §9.4 and DECISIONS §3: the payload is built on the immutable user id rather than on a
 * username, so a printed or saved code outlives a rename -- usernames are released for reuse
 * 30 days after a change, and a code keyed to one would eventually point at a stranger.
 */
@Composable
internal fun QrDialog(
    state: QrUiState,
    onDismiss: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val clipboard = LocalClipboardManager.current
    var copied by remember(state.qr?.payload) { mutableStateOf(false) }

    ProfileDialog(onDismiss = onDismiss) {
        Text(
            text = stringResource(Res.string.profile_qr_entry),
            style = MaterialTheme.typography.titleLarge,
            color = colors.textPrimary,
        )
        Spacer(Modifier.height(Space.two))

        when {
            state.loading -> Box(
                // Bounded on purpose: SwetLoading fills whatever it is given, and an unbounded
                // spinner inside a dialog stretches the dialog to the height of the screen.
                modifier = Modifier
                    .fillMaxWidth()
                    .height(DialogBusyHeight),
                contentAlignment = Alignment.Center,
            ) {
                SwetLoading()
            }

            state.error != null -> SwetErrorBanner(message = state.error.resolve())

            state.qr != null -> {
                val code = rememberSwetImage(state.qr.imageUrl, contentScale = ContentScale.Fit)
                val hasCode = code.phase == SwetImagePhase.Loaded

                Text(
                    // The copy now DESCRIBES what is on the screen instead of promising it. The
                    // sheet used to say "let someone scan it from your screen" whether or not a
                    // code had rendered, and the server's advertised image URL currently 404s --
                    // so the one sentence a user read was the one thing that was not true.
                    text = if (hasCode) {
                        stringResource(Res.string.profile_qr_scan_or_share)
                    } else {
                        stringResource(Res.string.profile_qr_link_only)
                    },
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.textSecondary,
                )
                Spacer(Modifier.height(Space.two))

                if (hasCode) {
                    QrImage(image = code)
                }

                // Still selectable, and still shown even when the code above it renders: the link
                // is the half of this that can be pasted into a message, and scanning needs a
                // second device.
                SelectionContainer {
                    Text(
                        text = state.qr.payload,
                        style = MaterialTheme.typography.bodySmall,
                        color = colors.textPrimary,
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(Radius.sm))
                            .background(colors.card)
                            .padding(Space.two),
                    )
                }

                Spacer(Modifier.height(Space.two))
                // The mechanism the copy asks for. "Share this link" with nothing but a text box
                // under it left the user to select a URL by hand in a dialog -- which is possible,
                // via the SelectionContainer above, and is not what anybody would call sharing.
                //
                // Clipboard rather than a system share sheet: a share sheet needs a platform
                // launcher on both Android and iOS, and `:shared` has no such abstraction. This
                // one is Compose's own and works identically on both.
                SwetPrimaryButton(
                    text = if (copied) {
                        stringResource(Res.string.profile_qr_link_copied)
                    } else {
                        stringResource(Res.string.profile_qr_copy_link)
                    },
                    onClick = {
                        clipboard.setText(AnnotatedString(state.qr.payload))
                        copied = true
                    },
                )
            }
        }

        Spacer(Modifier.height(Space.one))
        SwetSecondaryButton(text = stringResource(Res.string.profile_close), onClick = onDismiss)
    }
}

/**
 * The rendered QR code, IF the server has one.
 *
 * DRAWN ONLY ON SUCCESS -- no skeleton, no error text, no reserved space. That is unusual enough to
 * need justifying, and the justification is that today this URL is a placeholder: `ProfileService`
 * builds `"$publicBaseUrl/qr/u/$userId.png"` and says in its own comment that nothing in the
 * repository serves it, which a request confirms with a 404. A skeleton would flash and vanish on
 * every open, and a "Code unavailable" box would report a failure the user cannot act on and that
 * is not really theirs.
 *
 * What CHANGED is that the caller now asks the same question and adjusts its copy: the sheet no
 * longer offers to let somebody "scan it from your screen" when there is nothing on the screen to
 * scan. The load is therefore started by [QrDialog] and the result passed in here, rather than
 * being started twice for two different decisions about one image.
 *
 * SQUARE and on a WHITE fill, neither of which is a style choice: a QR code is only scannable if
 * its modules stay square, and scanners need dark-on-light. `colors.card` is near-black in the dark
 * theme, and a code rendered with transparent quiet zones onto it reads as inverted and does not
 * decode on most phones. This is the same exception the design system already makes for content
 * drawn on a saturated fill.
 *
 * Bounded rather than `fillMaxWidth`: this sits inside a dialog, and a code that grew with the
 * screen would be a full-width block on a tablet for no gain -- past a few hundred dp a camera
 * gains nothing from more pixels.
 */
@Composable
private fun QrImage(image: SwetImage) {
    Box(
        modifier = Modifier
            .size(QrCodeSize)
            .clip(RoundedCornerShape(Radius.sm))
            .background(Color.White),
        contentAlignment = Alignment.Center,
    ) {
        Image(
            painter = image.painter,
            // The payload is printed underneath in full; describing the picture as well would make
            // a screen reader read the same link twice.
            contentDescription = null,
            // Fit, never Crop: cropping a QR code removes modules and it stops decoding.
            contentScale = ContentScale.Fit,
            modifier = Modifier
                .fillMaxSize()
                // The quiet zone. A code printed flush to the edge of its own white field is the
                // most common reason a scanner refuses one.
                .padding(Space.one)
                .swetAppear(),
        )
    }

    // Emitted HERE rather than at the call site, so that the gap disappears with the code: a
    // caller that bracketed this in spacers would leave a double-height hole on every open for as
    // long as the URL 404s.
    Spacer(Modifier.height(Space.two))
}

// ---------------------------------------------------------------------------------------------
// Shared primitives for the ui.profile package
// ---------------------------------------------------------------------------------------------

/** How tall a dialog's spinner slot is. Token arithmetic, so it tracks the spacing scale. */
private val DialogBusyHeight: Dp = Space.eight + Space.four

/** Token arithmetic again: 192dp, comfortably scannable and inside a dialog on a small phone. */
private val QrCodeSize: Dp = Space.eight * 3

/**
 * Whether arriving at the profile tab has to read it again.
 *
 * Pure and internal so the rule is exercised without a renderer -- there is no emulator on this
 * host, so a `LaunchedEffect` is otherwise verified by reading it, which is what let the missing
 * re-entry read ship in the first place.
 *
 * TWO CASES, and the first is the reported one: an [error] means the last attempt failed, and a
 * banner about a request that failed ten minutes ago is worth exactly one retry when the user comes
 * back to look. `!`[loadedOnce] means nothing has settled, which is the same argument one step
 * earlier.
 *
 * A LOADED PROFILE WITH NO ERROR IS DELIBERATELY LEFT ALONE. Re-reading it on every entry would
 * spend a request per tab switch to redraw values that change only when this user changes them --
 * on the one screen in the app whose content the user themselves owns.
 */
internal fun profileNeedsReloadOnEntry(error: UiText?, loadedOnce: Boolean): Boolean =
    error != null || !loadedOnce

/**
 * SPS §13.1's joined date, or NULL when there is nothing truthful to print.
 *
 * Null rather than a placeholder is the whole rule. `MyProfile.joinedAt` is nullable only because
 * a date the client could not parse must not become "Joined --" or, far worse, an epoch that tells
 * somebody they joined in 1970 -- the same argument `ScheduledDeletion.scheduledFor` makes, on a
 * screen where a wrong answer is merely embarrassing rather than unrecoverable.
 *
 * Pure and internal so the rule is exercised without a renderer.
 *
 * TWO SPELLINGS OF ONE FACT WERE REPORTED HERE, AND THEY TURNED OUT TO BE TWO FACTS. This line read
 * "Joined 05.08.2026" while the group member card read "Joined on 12.03.2026" from
 * `group_member_joined_on`, so English said the same thing two ways. The WORDING is now unified --
 * both are "Joined on <date>" -- but the KEYS stay separate, because the facts are not the same
 * one: the member card says how long an account has been in a GROUP and this says how long the
 * account has existed. English can blur that; Russian cannot. `group_member_joined_on` is
 * «В группе с %1$s» and this is «В Swetgram с %1$s», and no single sentence can be both.
 *
 * The DATE remains an argument and never part of the sentence: `LocalDay.render()` produced it,
 * every language prints the same digits, and a language that puts the date first can say so in its
 * own file.
 */
internal fun joinedLabel(profile: MyProfile, zone: TimeZone): UiText? =
    profile.joinedAt?.let { Res.string.profile_joined_on.text(LocalDay.of(it, zone).render()) }

/**
 * The identity block: avatar, display name, @username, bio.
 *
 * Shared by [ProfileScreen] and `PublicProfileScreen` so the two cannot drift -- the public
 * profile is the same person rendered under different permissions, and it would be strange for
 * their name to be a different size depending on who is looking.
 *
 * [isOnline] is nullable and the three values mean three different things: true draws the dot,
 * false draws none, and NULL means the viewer is not allowed to know -- which also draws none,
 * but for a reason the caller must not collapse into "offline" (see `domain.UserProfile`).
 */
@Composable
internal fun ProfileIdentity(
    displayName: String,
    username: String,
    bio: String?,
    isOnline: Boolean?,
    surfaceColor: Color,
    modifier: Modifier = Modifier,
    presenceLine: String? = null,
    /** The stored photo. Null, or unreachable, leaves the initials showing. */
    avatarUrl: String? = null,
    hasBlackBadge: Boolean = false,
) {
    val colors = SwetgramThemeTokens.colors

    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SwetAvatar(
            displayName = displayName,
            // §11.5's profile size. The dot grows with it, which the component handles.
            size = Sizes.avatarProfile,
            isOnline = isOnline == true,
            // §11.5: the dot's border takes the colour of what the avatar sits ON, or it reads
            // as a ring rather than as a cut-out.
            surfaceColor = surfaceColor,
            avatarUrl = avatarUrl,
            // Decorative here: whenever the dot is drawn, [presenceLine] below is already saying
            // "Online" in words, and a described dot would have a screen reader read it twice.
            onlineContentDescription = null,
        )

        Spacer(Modifier.height(Space.two))

        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.Center) {
            Text(
                text = displayName,
                style = MaterialTheme.typography.headlineSmall,
                color = colors.textPrimary,
                textAlign = TextAlign.Center,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f, fill = false),
            )
            if (hasBlackBadge) {
                Spacer(Modifier.width(Space.one))
                // This is the reader's own status, not a promotional entrance. The public marks
                // beside other people's names open Swetgram Black; this one stays informational.
                SwetBlackBadge(size = 20.dp, opensPremium = false)
            }
        }

        Spacer(Modifier.height(Space.half))

        Text(
            // `handle_prefixed` rather than a Kotlin template, and it is the shared key the chat
            // header, the chat list, the new-chat list and the groups list all already use. The
            // `@` is punctuation around a value, not copy -- but it is punctuation the app must
            // spell ONE way, and a template here is the one site that could drift alone.
            text = stringResource(Res.string.handle_prefixed, username),
            style = MaterialTheme.typography.bodyMedium,
            color = colors.textSecondary,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )

        // BIO BEFORE PRESENCE, which is the order both SPS §13.1 and §13.2 list and the order SDS
        // §15.1 repeats: avatar, name, handle, bio. It also puts the one line that never changes
        // next to the identity that never changes, and leaves the volatile "last seen" underneath
        // where a refresh rewriting it does not shove the person's own words around.
        //
        // §5.3's `body.medium` -- the default body role -- in primary text: this is the profile's
        // only piece of prose and the main thing a reader has to go on before the Block/Report
        // choice that sits under it on the public screen.
        if (!bio.isNullOrBlank()) {
            Spacer(Modifier.height(Space.two))
            Text(
                text = bio,
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textPrimary,
                textAlign = TextAlign.Center,
            )
        }

        if (presenceLine != null) {
            Spacer(Modifier.height(if (bio.isNullOrBlank()) Space.half else Space.two))
            Text(
                text = presenceLine,
                style = MaterialTheme.typography.labelMedium,
                color = colors.textSecondary,
                textAlign = TextAlign.Center,
            )
        }
    }
}

/** A settings-style group heading. §5.3's `label.medium`, in secondary text. */
@Composable
internal fun ProfileSectionHeader(
    text: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = SwetgramThemeTokens.colors.textSecondary,
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = Space.two, vertical = Space.one),
    )
}

/**
 * A row that leads somewhere: a title, an optional current value, and a disclosure chevron.
 *
 * Not `SwetListRow`: that is SDS §12.1's 72dp CONVERSATION row, it requires a leading avatar slot
 * and its trailing slot is a timestamp and an unread badge. Forcing a settings entry through it
 * would mean passing an empty avatar and abusing the badge, which is how a design system starts
 * lying about what its components mean.
 *
 * 48dp minimum ([TouchTarget.recommended]) via `defaultMinSize`, so a large system font grows the
 * row instead of clipping it.
 *
 * [swetPressable] and not `Modifier.clickable`: a bare `clickable` under a Material3 theme inherits
 * `LocalIndication`, which is Material's ripple -- an expanding circle on a duration that is not
 * §19.2's, next to rows elsewhere in this app that use §25.1's state layer. SwetMotion's own
 * documentation records why that is the one argument that matters.
 */
@Composable
internal fun ProfileNavRow(
    title: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    value: String? = null,
    enabled: Boolean = true,
    showChevron: Boolean = true,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = modifier
            .fillMaxWidth()
            .swetPressable(onClick = onClick, enabled = enabled, role = Role.Button)
            .defaultMinSize(minHeight = TouchTarget.recommended)
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.bodyMedium,
            color = if (enabled) colors.textPrimary else colors.textTertiary,
            modifier = Modifier.weight(1f),
        )

        if (value != null) {
            Spacer(Modifier.width(Space.one))
            Text(
                text = value,
                style = MaterialTheme.typography.bodySmall,
                color = colors.textSecondary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }

        if (showChevron) {
            Spacer(Modifier.width(Space.one))
            ProfileChevron(enabled = enabled)
        }
    }
}

/**
 * The disclosure chevron.
 *
 * It is the design system's own back chevron turned around rather than a new drawing, so the
 * stroke width and the rounded terminals of SDS §9.3 stay identical across the app -- a
 * hand-drawn twin would drift the first time either is touched.
 *
 * Decorative: the row's title already names the destination, and the whole row is one button.
 */
@Composable
private fun ProfileChevron(enabled: Boolean) {
    val colors = SwetgramThemeTokens.colors
    SwetIcon(
        icon = SwetIcons.ChevronLeft,
        tint = if (enabled) colors.textSecondary else colors.border,
        contentDescription = null,
        modifier = Modifier.rotate(HALF_TURN_DEGREES),
        size = Sizes.iconSmall,
    )
}

private const val HALF_TURN_DEGREES = 180f

/**
 * A row that toggles or selects something, with the control drawn by the caller.
 *
 * The whole row is the target rather than just the control -- a 24dp radio button is well under
 * SDS §6.3's 44dp minimum, and the accessible pattern is for the ROW to carry the click and the
 * role while the control stays inert. Callers therefore pass `toggleable`/`selectable` in
 * [modifier] and give their control a null callback.
 */
@Composable
internal fun ProfileControlRow(
    title: String,
    modifier: Modifier = Modifier,
    supporting: String? = null,
    control: @Composable () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = modifier
            .fillMaxWidth()
            .defaultMinSize(minHeight = TouchTarget.recommended)
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                text = title,
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textPrimary,
            )
            if (supporting != null) {
                Spacer(Modifier.height(Space.half))
                Text(
                    text = supporting,
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.textSecondary,
                )
            }
        }

        Spacer(Modifier.width(Space.two))
        control()
    }
}

/**
 * The dialog shell: SDS §7.1's `radius.xl`, on the main surface, at the screen margin.
 *
 * Built on `androidx.compose.ui.window.Dialog` rather than Material3's `AlertDialog` because
 * `AlertDialog` lays its actions out as a horizontal row of text buttons, and the design system's
 * buttons ([SwetDestructiveButton], [SwetPrimaryButton], [SwetSecondaryButton]) are full-width by
 * definition -- SDS §11.1 gives no compact variant. Stacking them inside our own surface keeps
 * the specified buttons instead of substituting Material's.
 */
@Composable
internal fun ProfileDialog(
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    SwetDialog(onDismiss = onDismiss, modifier = modifier, content = content)
}

/**
 * SDS §11.1: "Use confirmation dialogs for destructive actions." This is that dialog.
 *
 * [destructive] picks the button, not the words. The copy is the caller's, because §18.2 wants
 * the sentence to name the actual consequence -- "Your account will be deleted in 30 days" is
 * useful where "Are you sure?" is not.
 *
 * Cancel is second and full width, in the same tap zone as the confirm button, which is why the
 * confirm button's colour has to carry the warning: on a small screen the thumb lands on the
 * bottom control, and that one must be the safe one.
 */
@Composable
internal fun ProfileConfirmDialog(
    title: String,
    body: String,
    confirmText: String,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    /**
     * DEFAULTED FROM THE RESOURCE, not from a literal. This one word is the way out of every
     * destructive dialog in the app -- groups, moderation and Discover all reach this component --
     * so localising the default localises seven call sites that never mention it, and none of them
     * has to change. A default expression in a `@Composable` is evaluated in the composition, so
     * `stringResource` is legal here.
     */
    cancelText: String = stringResource(Res.string.profile_cancel),
    destructive: Boolean = true,
    busy: Boolean = false,
) {
    SwetConfirmDialog(
        title = title,
        body = body,
        confirmText = confirmText,
        cancelText = cancelText,
        onConfirm = onConfirm,
        onDismiss = onDismiss,
        modifier = modifier,
        destructive = destructive,
        busy = busy,
    )
}
